import React, { useState, forwardRef } from "react";
import { Input } from "@/components/ui/input";
import { Eye, EyeOff } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Drop-in replacement for <Input type="password" /> that adds a
 * visibility toggle (eye icon) inside the field.
 *
 * Forward-refs the underlying <input> for forms that need it.
 *
 * Extra props:
 *  - testId — applied to the show/hide toggle button (the input itself
 *    receives whatever data-testid you pass via {...rest}).
 *  - inputTestId — convenience: applied to the input as data-testid.
 */
const PasswordInput = forwardRef(function PasswordInput(
  { className, testId, inputTestId, ...rest },
  ref,
) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <Input
        ref={ref}
        type={show ? "text" : "password"}
        className={cn("pr-10", className)}
        data-testid={inputTestId}
        {...rest}
      />
      <button
        type="button"
        tabIndex={-1}
        aria-label={show ? "Hide password" : "Show password"}
        onClick={() => setShow((v) => !v)}
        className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 inline-flex items-center justify-center rounded-sm text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
        data-testid={testId || "password-toggle"}
      >
        {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  );
});

export default PasswordInput;
